import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/fieldops/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Layers, KeyRound, CalendarClock, Plus, Trash2, Barcode } from "lucide-react";
import FrsCodesSection from "./FrsCodesSection";

// Reusable in-app confirmation toast (same behaviour as the field settings page).
function confirmToast(message, onConfirm, { confirmLabel = "Delete", danger = true } = {}) {
  toast(message, {
    duration: 8000,
    action: { label: confirmLabel, onClick: () => onConfirm() },
    cancel: { label: "Cancel" },
    classNames: danger ? { actionButton: "!bg-red-600 !text-white hover:!bg-red-700" } : undefined,
  });
}

// ---------------- Scope Codes sub-tab ----------------
function ScopeCodesSection() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => { const r = await api.get("/scope-codes"); setRows(r.data || []); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.patch(`/scope-codes/${editing.id}`, editing);
      else await api.post("/scope-codes", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id, code) => {
    confirmToast(`Delete scope ${code || ""}?`, async () => {
      try { await api.delete(`/scope-codes/${id}`); load(); }
      catch { toast.error("Delete failed"); }
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" className=""
          onClick={() => setEditing({ code: "", trade: "", cost_code: "", unit_of_measure: "", project_manager: "", foreman: "" })}
          data-testid="scope-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add scope
        </Button>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-auto max-h-[70vh]">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr className="text-xs text-gray-500 uppercase tracking-wider">
              <th className="text-left px-3 py-2 font-medium w-24">Code</th>
              <th className="text-left px-3 py-2 font-medium">Trade</th>
              <th className="text-left px-3 py-2 font-medium">Cost code</th>
              <th className="text-left px-3 py-2 font-medium">Unit</th>
              <th className="text-left px-3 py-2 font-medium">PM</th>
              <th className="text-left px-3 py-2 font-medium">Foreman</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-xs font-semibold">{r.code}</td>
                <td className="px-3 py-2">{r.trade}</td>
                <td className="px-3 py-2 text-xs text-gray-600">{r.cost_code}</td>
                <td className="px-3 py-2 text-xs text-gray-600">{r.unit_of_measure}</td>
                <td className="px-3 py-2 text-xs text-gray-600">{r.project_manager}</td>
                <td className="px-3 py-2 text-xs text-gray-600">{r.foreman}</td>
                <td className="px-3 py-2 text-right">
                  <Button variant="ghost" size="sm" onClick={() => setEditing({ ...r })} data-testid={`scope-edit-${r.id}`}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => del(r.id, r.code)}><Trash2 className="w-3.5 h-3.5 text-gray-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
            <header className="px-4 py-3 border-b border-gray-200"><h3 className="text-sm font-semibold">{editing.id ? "Edit scope" : "Add scope"}</h3></header>
            <div className="p-4 grid grid-cols-2 gap-3">
              <div><label className="text-xs text-gray-600">Code *</label><Input value={editing.code || ""} onChange={(e) => setEditing({ ...editing, code: e.target.value })} /></div>
              <div><label className="text-xs text-gray-600">Trade</label><Input value={editing.trade || ""} onChange={(e) => setEditing({ ...editing, trade: e.target.value })} /></div>
              <div><label className="text-xs text-gray-600">Cost code</label><Input value={editing.cost_code || ""} onChange={(e) => setEditing({ ...editing, cost_code: e.target.value })} /></div>
              <div><label className="text-xs text-gray-600">Unit of measure</label><Input value={editing.unit_of_measure || ""} onChange={(e) => setEditing({ ...editing, unit_of_measure: e.target.value })} /></div>
              <div><label className="text-xs text-gray-600">Project manager</label><Input value={editing.project_manager || ""} onChange={(e) => setEditing({ ...editing, project_manager: e.target.value })} /></div>
              <div><label className="text-xs text-gray-600">Foreman</label><Input value={editing.foreman || ""} onChange={(e) => setEditing({ ...editing, foreman: e.target.value })} /></div>
            </div>
            <footer className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button className="" onClick={save} data-testid="scope-save">Save</Button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Status Keys sub-tab ----------------
function StatusKeysSection() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => { const r = await api.get("/status-keys"); setRows(r.data || []); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.patch(`/status-keys/${editing.id}`, editing);
      else await api.post("/status-keys", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id, code) => {
    confirmToast(`Delete status key ${code || ""}?`, async () => {
      try { await api.delete(`/status-keys/${id}`); load(); }
      catch { toast.error("Delete failed"); }
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" className="" onClick={() => setEditing({ code: "", meaning: "" })} data-testid="status-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add status key
        </Button>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-auto max-h-[70vh]">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr className="text-xs text-gray-500 uppercase tracking-wider">
              <th className="text-left px-3 py-2 font-medium w-32">Code</th>
              <th className="text-left px-3 py-2 font-medium">Meaning</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-xs font-semibold">{r.code}</td>
                <td className="px-3 py-2 text-gray-700">{r.meaning}</td>
                <td className="px-3 py-2 text-right">
                  <Button variant="ghost" size="sm" onClick={() => setEditing({ ...r })}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => del(r.id, r.code)}><Trash2 className="w-3.5 h-3.5 text-gray-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <header className="px-4 py-3 border-b border-gray-200"><h3 className="text-sm font-semibold">{editing.id ? "Edit status key" : "Add status key"}</h3></header>
            <div className="p-4 grid grid-cols-1 gap-3">
              <div><label className="text-xs text-gray-600">Code *</label><Input value={editing.code || ""} onChange={(e) => setEditing({ ...editing, code: e.target.value })} /></div>
              <div><label className="text-xs text-gray-600">Meaning</label><Input value={editing.meaning || ""} onChange={(e) => setEditing({ ...editing, meaning: e.target.value })} /></div>
            </div>
            <footer className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button className="" onClick={save} data-testid="status-save">Save</Button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}


// GC-schedule keyword synonyms — drive the Upload Schedule mapping proposals.
function ScheduleKeywordsSection() {
  const [rows, setRows] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [kw, setKw] = useState("");
  const [code, setCode] = useState("");
  const [kind, setKind] = useState("scope");
  const [anchors, setAnchors] = useState([]);
  const load = () => {
    api.get("/scope-keywords").then((r) => setRows(r.data || [])).catch(() => {});
    api.get("/scope-codes").then((r) => setScopes(r.data || [])).catch(() => {});
    api.get("/gc-rules").then((r) => setAnchors(r.data.anchors || [])).catch(() => {});
  };
  useEffect(load, []);
  const add = async () => {
    if (!kw.trim() || !code.trim()) { toast.error("Keyword and scope code are required"); return; }
    try {
      await api.post("/scope-keywords", { keyword: kw.trim(), scope_code: code.trim(), kind });
      setKw(""); setCode(""); load(); toast.success("Keyword added");
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/scope-keywords/${id}`); load(); } catch { toast.error("Delete failed"); }
  };
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="settings-schedule-keywords">
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">Schedule keywords</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          When a GC schedule activity contains one of these phrases, it is proposed as that ABS scope (e.g. "toilet partitions" → TC).
          Anchor aliases name the GC milestones the date rules hang on (e.g. "tape and finish" → Drywall finish &amp; prime).
        </p>
        <div className="flex flex-wrap items-end gap-2 mt-3">
          <select value={kind} onChange={(e) => { setKind(e.target.value); setCode(""); }} data-testid="keyword-kind-select"
                  className="h-8 border border-gray-300 rounded-md px-2 text-sm bg-white">
            <option value="scope">ABS scope</option>
            <option value="anchor">Anchor activity</option>
          </select>
          <input
            data-testid="keyword-input"
            value={kw}
            onChange={(e) => setKw(e.target.value)}
            placeholder="activity phrase e.g. toilet partitions"
            className="h-8 w-72 border border-gray-300 rounded-md px-2 text-sm bg-white"
          />
          <input
            data-testid="keyword-scope-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder={kind === "anchor" ? "Anchor key" : "Scope code"}
            list={kind === "anchor" ? "anchor-key-list" : "scope-code-list"}
            className="h-8 w-44 border border-gray-300 rounded-md px-2 text-sm bg-white"
          />
          <datalist id="scope-code-list">
            {scopes.map((sc) => <option key={sc.id} value={sc.code}>{sc.trade}</option>)}
          </datalist>
          <datalist id="anchor-key-list">
            {anchors.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </datalist>
          <button data-testid="keyword-add-btn" onClick={add}
            className="h-8 px-3 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700">Add</button>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr className="text-[10px] uppercase tracking-wider text-gray-500">
            <th className="text-left px-4 py-2 font-medium">Keyword phrase</th>
            <th className="text-left px-3 py-2 font-medium">Kind</th>
            <th className="text-left px-3 py-2 font-medium">Scope / anchor</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500">No keywords yet.</td></tr>
          ) : [...rows].sort((a, b) => (a.kind === "anchor") - (b.kind === "anchor") || a.keyword.localeCompare(b.keyword)).map((r) => (
            <tr key={r.id} className="border-t border-gray-100" data-testid={`keyword-row-${r.id}`}>
              <td className="px-4 py-1.5 text-gray-800">{r.keyword}</td>
              <td className="px-3 py-1.5 text-[10px] uppercase text-gray-500">{r.kind === "anchor" ? "anchor" : "scope"}</td>
              <td className="px-3 py-1.5 font-medium text-gray-700">
                {r.scope_code || r.scope_type}
                {r.kind === "anchor" && <span className="text-gray-400 font-normal"> · {anchors.find((a) => a.key === r.scope_code)?.label || ""}</span>}
              </td>
              <td className="px-3 py-1.5 text-right">
                <button data-testid={`keyword-delete-${r.id}`} onClick={() => remove(r.id)}
                  className="text-xs text-red-600 hover:underline">Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// The three field code tables under one roof. They still read and write the field
// endpoints (/api/field/scope-codes, /scope-keywords, /status-keys) — only the tab
// they live under moved, so everything wired to them in Field Operations is untouched.
export default function FieldCodesPage() {
  return (
    <div className="page-body" data-testid="field-codes-page">
      <div className="mb-5">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>Field Codes</h1>
        <p className="text-sm text-slate-500 mt-1">
          Scope codes, GC-schedule keyword synonyms, submittal status keys and the FRS item
          acronyms — the shared vocabulary the field, the schedule importer and the FRS all read.
        </p>
      </div>
      <Tabs defaultValue="scopes" className="w-full">
        <TabsList className="bg-white border border-slate-200 rounded-md h-auto min-h-10 px-1 flex-wrap max-w-full justify-start">
          <TabsTrigger value="scopes" data-testid="field-codes-tab-scopes">
            <Layers className="w-3.5 h-3.5 mr-1" />Scope Codes
          </TabsTrigger>
          <TabsTrigger value="keywords" data-testid="field-codes-tab-keywords">
            <CalendarClock className="w-3.5 h-3.5 mr-1" />Schedule Keywords
          </TabsTrigger>
          <TabsTrigger value="status" data-testid="field-codes-tab-status">
            <KeyRound className="w-3.5 h-3.5 mr-1" />Status Keys
          </TabsTrigger>
          <TabsTrigger value="frs" data-testid="field-codes-tab-frs">
            <Barcode className="w-3.5 h-3.5 mr-1" />FRS Codes
          </TabsTrigger>
        </TabsList>
        <div className="mt-4">
          <TabsContent value="scopes"><ScopeCodesSection /></TabsContent>
          <TabsContent value="keywords"><ScheduleKeywordsSection /></TabsContent>
          <TabsContent value="status"><StatusKeysSection /></TabsContent>
          <TabsContent value="frs"><FrsCodesSection /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
